import { useEffect, useState } from 'react';
import { Button, Empty, Input, Modal, Spin, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useReportConfig, useSaveReportConfig } from '../../hooks/useReports';
import CsvDataFilterBuilder from './CsvDataFilterBuilder';

function nextKey() {
    return `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function toDraft(projectTypes) {
    return (projectTypes || []).map(type => ({
        key: type._id || nextKey(),
        _id: type._id,
        name: type.name,
        filters: type.filters || [],
    }));
}

export default function ConfigureReportModal({ companyCode, open, onClose }) {
    const { data, isFetching } = useReportConfig(companyCode, open);
    const save = useSaveReportConfig();
    const [draft, setDraft] = useState([]);
    const [name, setName] = useState('');
    const [selectedKey, setSelectedKey] = useState(null);

    useEffect(() => {
        if (!open) return;
        const next = toDraft(data?.projectTypes);
        setDraft(next);
        setSelectedKey(current => {
            if (current && next.some(type => type.key === current)) return current;
            return next[0]?.key || null;
        });
    }, [open, data]);

    const selected = draft.find(type => type.key === selectedKey) || null;

    const addType = () => {
        const trimmed = name.trim();
        if (!trimmed) return;
        if (draft.some(type => type.name.toLowerCase() === trimmed.toLowerCase())) {
            message.warning('That project type already exists');
            return;
        }
        const type = { key: nextKey(), name: trimmed, filters: [] };
        setDraft([...draft, type]);
        setSelectedKey(type.key);
        setName('');
    };

    const updateSelected = patch => {
        setDraft(current => current.map(type => (type.key === selectedKey ? { ...type, ...patch } : type)));
    };

    const onSave = () => {
        save.mutate(
            {
                companyCode,
                projectTypes: draft.map(type => ({
                    ...(type._id ? { _id: type._id } : {}),
                    name: type.name,
                    filters: type.filters || [],
                })),
            },
            { onSuccess: onClose }
        );
    };

    return (
        <Modal
            title={`Configure report · ${companyCode}`}
            open={open}
            onCancel={onClose}
            onOk={onSave}
            okText="Save"
            confirmLoading={save.isLoading}
            destroyOnClose
            width={960}
            wrapClassName="reports-config-modal"
            bodyStyle={{ minHeight: 520 }}
        >
            <p className="reports-config-hint">
                Add project types, then stack csvData filters on the selected type. Values are sampled from recent SKUs.
            </p>
            <div className="reports-config-add">
                <Input
                    value={name}
                    placeholder="Project type name"
                    onChange={event => setName(event.target.value)}
                    onPressEnter={addType}
                />
                <Button icon={<PlusOutlined />} onClick={addType}>
                    Add
                </Button>
            </div>
            {isFetching && !draft.length ? (
                <Spin />
            ) : draft.length ? (
                <div className="reports-config-layout">
                    <ul className="reports-config-list">
                        {draft.map(type => (
                            <li
                                key={type.key}
                                className={type.key === selectedKey ? 'is-active' : ''}
                            >
                                <button type="button" onClick={() => setSelectedKey(type.key)}>
                                    <strong>{type.name}</strong>
                                    <em>
                                        {(type.filters || []).length} filter
                                        {(type.filters || []).length === 1 ? '' : 's'}
                                    </em>
                                </button>
                                <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => {
                                        const next = draft.filter(item => item.key !== type.key);
                                        setDraft(next);
                                        if (selectedKey === type.key) setSelectedKey(next[0]?.key || null);
                                    }}
                                />
                            </li>
                        ))}
                    </ul>
                    <div className="reports-config-detail">
                        {selected ? (
                            <CsvDataFilterBuilder
                                companyCode={companyCode}
                                filters={selected.filters}
                                onChange={filters => updateSelected({ filters })}
                            />
                        ) : (
                            <Empty description="Select a project type" />
                        )}
                    </div>
                </div>
            ) : (
                <Empty description="No project types yet" />
            )}
        </Modal>
    );
}
